import { queryClient } from "@shared/api/queries/queryClient";
import { CustomTooltip } from "@shared/components/CustomTooltip";
import { DynamicForm } from "@shared/components/DynamicForm";
import { toast } from "@shared/hooks/use-toast";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import {
  FormConfig,
  FormField,
  FormService,
} from "@shared/services/FormServices";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { useEffect, useState } from "react";
import {
  useChoices,
  useGetMeterModel,
  useMeterManufacturers,
  usePostBulkMeter,
} from "../../hooks";

export const AddBulkMeters = ({ isOpen, onClose }) => {
  const [selectedMeterType, setSelectedMeterType] = useState("");
  const [selectedManufacturer, setSelectedManufacturer] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    meterTypes: [],
    manufacturers: [],
  });

  const remoteUtilityId = useRemoteUtilityId();
  const { data: meterTypes } = useChoices({ choice: "meter_type" });

  const {
    data: manufacturerData,
    isLoading: manufacturersLoading,
    refetch: refetchManufacturers,
  } = useMeterManufacturers({
    remote_utility_id: remoteUtilityId,
  });

  const { data: modelData, error: modelsError } = useGetMeterModel(
    selectedMeterType,
    selectedManufacturer,
  );
  const { mutate: uploadMeters } = usePostBulkMeter();

  const modelsOptions =
    modelData?.map((model) => ({
      label: model.meterModel,
      value: model.id,
    })) || [];

  useEffect(() => {
    if (!isOpen) return;
    const meterTypesRaw = meterTypes;

    if (meterTypesRaw && manufacturerData?.results) {
      const transformMeterTypes = (arr: any[]) =>
        arr.map((item) => ({
          label: item.value,
          value: item.key,
        }));

      const transformManufacturers = (arr: any[]) =>
        arr.map((item) => ({
          label: item.name,
          value: item.id, // This stores the ID as value
        }));

      setFilters({
        meterTypes: transformMeterTypes(meterTypesRaw),
        manufacturers: transformManufacturers(manufacturerData.results),
      });
    }
  }, [isOpen, meterTypes, manufacturerData]);

  const fields: FormField[] = [
    {
      name: "entryMethod",
      type: "radio",
      options: [
        { value: "manual", label: "Manual Entry" },
        { value: "csv", label: "CSV Upload" },
      ],
      defaultValue: "manual",
      className: "col-span-2 w-full flex flex-col justify-start",
    },
    {
      name: "meter_type",
      type: "select",
      label: "Meter Type",
      required: true,
      placeholder: "Select meter type",
      options: filters.meterTypes,
      size: "medium",
      variant: "outlined",
    },
    {
      name: "meter_make",
      type: "select",
      label: "Manufacturer",
      required: true,
      placeholder: "Select manufacturer",
      options: filters.manufacturers, // This contains {label: name, value: id}
      size: "medium",
      variant: "outlined",
    },
    {
      name: "meter_model",
      type: "select",
      label: "Model",
      required: true,
      placeholder:
        selectedMeterType && selectedManufacturer
          ? "Select model"
          : "Select meter type and manufacturer first",
      options: modelsOptions,
      size: "medium",
      variant: "outlined",
      disabled: !selectedMeterType || !selectedManufacturer,
    },
    {
      name: "initial_warehouse",
      type: "text",
      label: "Initial Warehouse",
      required: true,
      placeholder: "Enter warehouse location",
      minLength: 1,
      maxLength: 20,
      size: "medium",
      variant: "outlined",
    },
    {
      name: "deviceNumbers",
      type: "textarea",
      label: "Device Numbers (one per line)",
      placeholder: "Enter device numbers, one per line",
      cols: 50,
      // required: true,
      size: "medium",
      variant: "outlined",
      showWhen: (formValues) => formValues.entryMethod === "manual",
      customValidation: (value) => {
        if (value) {
          const lines = value.split("\n").filter((line) => line.trim());
          if (lines.length === 0) {
            return "Please enter at least one device number";
          }
        }
        return true;
      },
      className: "col-span-2 ",
    },
    {
      name: "csv_file",
      type: "file",
      label: "CSV File Upload",
      accept: ".csv",
      multiple: false,
      size: "medium",
      helperText:
        "CSV must include device numbers. Type, manufacturer, model, and warehouse will be applied to all imported meters.",
      showWhen: (formValues) => formValues.entryMethod === "csv",
      customValidation: (value) => {
        if (value && value[0]) {
          const file = value[0];
          if (!file.name.toLowerCase().endsWith(".csv")) {
            return "Please upload a CSV file";
          }
        }
        return true;
      },
      className: "col-span-2",
      classes: {
        file: "border p-2 rounded-md w-full",
      },
    },
  ];

  const handleSubmit = async (data) => {
    try {
      setFormSubmitting(true);

      const {
        entryMethod,
        deviceNumbers,
        meter_make, // This will be the manufacturer ID
        csv_file,
        meter_model,
        ...rest
      } = data;

      if (
        entryMethod === "manual" &&
        (!deviceNumbers || deviceNumbers.trim() === "")
      ) {
        toast({
          title: "Device numbers are required for manual entry",
          variant: "destructive",
        });
        return;
      }

      if (entryMethod === "csv" && (!csv_file || csv_file.length === 0)) {
        toast({
          title: "CSV file is required for CSV upload",
          variant: "destructive",
        });
        return;
      }

      // Convert manufacturer ID back to name for the API
      const manufacturerName = manufacturerData?.results?.find(
        (item: any) => item.id,
      );

      const device_numbers = deviceNumbers?.trim();
      const payload = {
        ...rest,
        meter_model: meter_model, // This contains the model ID
        meter_make: manufacturerName.name, // This contains the manufacturer name
        remote_utility_id: remoteUtilityId,
      };
      const formData = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value instanceof File || value instanceof Blob) {
          formData.append(key, value);
        } else {
          formData.append(key, String(value));
        }
      });

      if (entryMethod === "manual") {
        formData.append("device_numbers", device_numbers);
      } else if (entryMethod === "csv") {
        const file = csv_file?.[0] || csv_file?.file || csv_file;

        if (file instanceof File) {
          formData.append("csv_file", file);
        } else {
          console.warn("csv_file is not a File instance:", file);
        }
      }

      uploadMeters(formData, {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["mx", "inventory"],
          });
        },
      });

      onClose?.();
    } catch (error) {
      console.error("Error adding meters:", error);
      alert("Error adding meters. Please try again.");
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleFieldChange = (field: string, data: any) => {
    switch (field) {
      case "meter_type":
        setSelectedMeterType(data);
        setSelectedManufacturer("");
        setSelectedModel("");
        break;
      case "meter_make":
        setSelectedManufacturer(data); // This stores the manufacturer ID
        setSelectedModel("");
        break;
      case "meter_model":
        setSelectedModel(data);
        break;
    }
  };

  const handleReset = () => {
    setSelectedMeterType("");
    setSelectedManufacturer("");
    setSelectedModel("");
  };

  const handleCancel = () => {
    setSelectedMeterType("");
    setSelectedManufacturer("");
    setSelectedModel("");
    onClose?.();
  };

  const formConfig: FormConfig = {
    fields: fields,
    shouldUnregister: true,
    classes: {
      fieldContainer: "flex flex-col gap-1 mb-4",
      label: "text-sm text-gray-800 font-semibold",
      input: "rounded-md border px-3 py-2 border-gray-300 w-full",
      select: "rounded-md border px-3 py-2 border-gray-300",
      textarea: "rounded-md border px-3 py-2 border-gray-300",
      error: "text-sm text-red-500 mt-1",
      helperText: "text-xs text-gray-500 mt-1",
    },
  };
  const bulkMeterAddForm = FormService.createForm(formConfig);

  const renderCustomFooter = () => (
    <div className="flex justify-end gap-3 pt-6 border-t">
      <button
        type="button"
        onClick={handleCancel}
        disabled={formSubmitting}
        className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        type="submit"
        onClick={bulkMeterAddForm.handleSubmit(handleSubmit)}
        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {formSubmitting ? "Adding Meters..." : "Add Meter"}
      </button>
    </div>
  );

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="w-full max-w-2xl max-h-[100vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="card-heading-text flex items-center gap-2">
            Bulk Add Meters
            <CustomTooltip
              message="Add meters in bulk by entering meter numbers manually or uploading a CSV file with a meter_no column"
              side="right"
            />
          </DialogTitle>
        </DialogHeader>

        <DynamicForm
          fields={fields}
          form={bulkMeterAddForm}
          gridColumns={2}
          responsiveColumns={{
            sm: 1,
            md: 2,
          }}
          config={formConfig}
          onSubmit={handleSubmit}
          onReset={handleReset}
          loading={formSubmitting}
          onFieldChange={(field, data) => handleFieldChange(field, data)}
          renderCustomFooter={renderCustomFooter}
        />
      </DialogContent>
    </Dialog>
  );
};
